<template>
  <div
    ref="triggersContainer"
    class="campaign-triggers"
    :class="{ 'campaign-triggers--block': parentTrigger }"
  >
    <div class="campaign-triggers-list" v-if="triggersExist">
      <field-block v-for="(triggerGroup, key) in sortedTriggers" :key="key">
        <div v-if="isCampaignTriggers" class="d-flex align-center" slot="title">
          <template v-if="triggerGroup.delay">
            <span class="circle-icon">{{ key }} </span>
            Days Delayed
          </template>
          <template v-else>
            <span class="circle-icon">
              <v-icon color="light">today</v-icon>
            </span>
            {{ convertDate(key) }}
          </template>
        </div>

        <div
          v-for="(items, triggerGroupKey) in triggerGroup.items"
          :key="triggerGroupKey"
          class="campaign-triggers__chunk"
        >
          <div class="time-separator" v-if="isCampaignTriggers">
            <span>{{ convertTime(items[0]) }}</span>
          </div>
          <draggable
            class="campaign-triggers__dropzone"
            v-model="sortedTriggers[key].items[triggerGroupKey]"
            @change="changeOrder($event, key, triggerGroupKey)"
            :group="dragGroupOptions"
            :disabled="disabled"
          >
            <campaign-trigger-card
              v-for="trigger in items"
              :key="trigger.id"
              :trigger="trigger"
              :campaignId="campaign.id"
              :disabled="disabled"
              :show-selection="showSelectionOnCards"
            />
          </draggable>
        </div>
      </field-block>

      <field-block v-if="isCampaignTriggers">
        <div slot="title" class="d-flex align-center">
          <span class="circle-icon">{{ nextDayDelay }} </span>
          Days Delayed
        </div>

        <div class="campaign-triggers__chunk">
          <draggable
            :value="[]"
            @change="createDelayedTrigger"
            :group="dragGroupOptions"
            :disabled="disabled"
            class="campaign-triggers__dropzone"
          >
            <div class="placeholder-block">
              Drop here to delay {{ nextDayDelay }} day(s)
            </div>
          </draggable>
        </div>
      </field-block>
    </div>

    <draggable
      v-else
      :value="[]"
      @change="changeOrder"
      :group="dragGroupOptions"
      :disabled="disabled"
      class="campaign-triggers__dropzone"
    >
      <div class="placeholder-block">
        No Campaign content yet.<br />
        Click or drag content to add to your campaign.
      </div>
    </draggable>

    <v-btn
      v-if="parentTrigger"
      color="appMain"
      :disabled="disabled"
      @click="createDefaultTrigger({ type: 'lesson' })"
    >
      New Extra Lesson
    </v-btn>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex';
import { format, fromUnixTime, isSameDay } from 'date-fns';
import draggable from 'vuedraggable';
import cloneDeep from 'lodash/cloneDeep';
import Selecto from 'selecto';

import CampaignTriggerCard from './single-trigger/CampaignTriggerCard';
import EventBus from '@/factories/eventBus';

export default {
  name: 'CampaignTriggers',
  components: {
    draggable,
    CampaignTriggerCard,
  },
  props: {
    campaign: {
      type: Object,
      default: () => ({}),
    },
    parentTrigger: {
      type: Object,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      sortedTriggers: [],
      loadingType: '',
      selecto: null,
      dragGroupOptions: {
        name: 'trigger',
        pull: false,
      },
    };
  },
  computed: {
    ...mapGetters([
      'triggers',
      'groupedTriggers',
      'triggerTypeDescription',
      'singleTrigger',
    ]),
    parentTriggerId() {
      return this.parentTrigger?.id;
    },
    isCampaignTriggers() {
      return !this.parentTriggerId;
    },
    isDripCampaign() {
      return this.campaign?.type === 'drip';
    },
    showSelectionOnCards() {
      return this.isCampaignTriggers && !this.disabled;
    },
    triggersExist() {
      return this.triggers.length;
    },
    nextDayDelay() {
      return this.triggers.reduce((acc, { delay = 0 }) => {
        return delay >= acc ? +delay + 1 : acc;
      }, 1);
    },
    delayTime() {
      return this.calculateDelayTime();
    },
  },
  watch: {
    showSelectionOnCards(enabled) {
      if (enabled) {
        this.$nextTick(() => this.initSelecto());
      } else {
        this.destroySelecto();
      }
    },
    triggers(newTriggers) {
      if (newTriggers.length !== this.sortedTriggers.length) {
        this.prepareTriggers();
      }
    },
  },
  methods: {
    ...mapActions(['updateTrigger', 'createTrigger']),

    ...mapMutations(['updateTriggerInList', 'updateTriggerSelection']),

    calculateDelayTime() {
      const currentDelay = this.nextDayDelay - 1;
      const prevDelayedTrigger = this.triggers.reduce((acc, trigger) => {
        if (
          +trigger.delay === currentDelay &&
          (!acc.start_time || trigger.start_time < acc.start_time)
        ) {
          return trigger;
        }
        return acc;
      }, {});

      let delayTime = prevDelayedTrigger.start_at;
      if (!delayTime || delayTime === '00:01') {
        delayTime = '09:00';
      }

      return delayTime;
    },

    getTriggerTitle(type) {
      let automation = this.triggerTypeDescription(type);
      return automation?.title || 'New Lesson';
    },

    createDefaultTrigger(trigger) {
      let order = 0;
      if (this.parentTriggerId) {
        order = this.triggers.filter(
          ({ parent_trigger }) => parent_trigger === this.parentTriggerId
        ).length;
      }

      this.createDraftTrigger(trigger.type, order, {});
    },

    createDraftTrigger(type, order, timing) {
      this.loadingType = type;

      const trigger = {
        name: this.getTriggerTitle(type),
        type: type,
        order: order + 1,
        start_at: '00:01',
        join_delay: false, //this.campaign.join_delay,
        ...timing,
        parent_trigger: this.parentTriggerId,
        campaign_id: this.campaign.id,
      };

      this.createTrigger(trigger).then(({ id }) => {
        this.loadingType = '';
        this.openEditTrigger(id);
      });
    },

    openEditTrigger(triggerId) {
      this.$router.push({
        name: 'campaign-trigger-edit',
        params: {
          id: triggerId,
        },
      });
    },

    convertDate(date) {
      let convertedDate = date.replace(/-/g, '/');
      return format(new Date(convertedDate), 'MMM do, yyyy');
    },

    convertTime(trigger) {
      const { join_delay, start_date, start_at, start_time } = trigger;
      if (!join_delay && !start_date && start_at === '00:01') {
        return 'Release Immediately';
      }

      const localeOptions = {
        hour: 'numeric',
        minute: 'numeric',
        hour12: true,
        timeZone: trigger.timezone,
        timeZoneName: 'short',
      };

      return fromUnixTime(start_time).toLocaleString('en-US', localeOptions);
    },

    prepareTriggers() {
      this.sortedTriggers = this.groupedTriggers(this.parentTriggerId);
    },

    updateOrder(delayKey, itemKey) {
      if (!delayKey || !itemKey) return;
      const triggers = this.sortedTriggers[delayKey].items[itemKey] || [];

      if (triggers.length <= 1) return;
      triggers.forEach((trigger, idx) => {
        if (trigger.id && trigger.order !== idx + 1) {
          this.updateTrigger({
            ...cloneDeep(trigger),
            order: idx + 1,
          });
        }
      });
    },

    createDelayedTrigger(e) {
      if (!e.added) return;
      this.createDraftTrigger(e.added.element.type, e.added.newIndex, {
        delay: this.nextDayDelay,
        start_at: this.delayTime,
        join_delay: this.isDripCampaign,
      });
    },

    changeOrder(e, delayKey, itemKey) {
      this.updateOrder(delayKey, itemKey);

      if (!e.added) return;

      let timing = null;
      if (delayKey && itemKey) {
        const triggers = this.sortedTriggers[delayKey].items[itemKey];
        timing = this.getTimingOptions(triggers.find((i) => i.id) || {});
      }

      this.createDraftTrigger(e.added.element.type, e.added.newIndex, timing);
    },

    async triggerScroll() {
      await this.$nextTick();

      if (this.previousRoute?.name !== 'campaign-trigger-edit') return;

      const triggerId = this.previousRoute.params.id;
      if (!triggerId) return;

      const triggerElement = document.getElementById(triggerId);
      const scrollElement = document.querySelector('.content-main');
      if (!triggerElement || !scrollElement) return;

      scrollElement.scroll({
        behavior: 'smooth',
        left: 0,
        top: triggerElement.offsetTop - 50,
      });
    },

    getTimingOptions(trigger) {
      const isDelayed = !isSameDay(
        new Date(this.campaign.created_at * 1000),
        new Date(this.campaign.start_time)
      );

      if (!trigger) return { join_delay: isDelayed };

      return {
        join_delay: trigger.join_delay,
        delay: trigger.delay,
        start_time: trigger.start_time,
        start_date: trigger.start_date,
        start_at: trigger.start_at,
      };
    },

    handleOrderChange({ delayKey, itemKey }) {
      this.updateOrder(delayKey, itemKey);
    },

    initSelecto() {
      if (!this.showSelectionOnCards) return;

      const container = this.$refs.triggersContainer;
      if (!container) return;

      this.destroySelecto();

      const dragSurface = container.closest('.content-main') || container;

      this.selecto = new Selecto({
        container: dragSurface,
        dragContainer: dragSurface,
        boundContainer: dragSurface,
        selectableTargets: ['.campaign-trigger-card'],
        selectByClick: false,
        selectFromInside: false,
        continueSelect: false,
        toggleContinueSelect: 'shift',
        keyContainer: dragSurface,
        hitRate: 50,
        dragCondition: (e) => {
          const target = e.inputEvent.target;
          if (
            target.closest('.campaign-trigger-card') ||
            target.closest('.item-card-drag') ||
            target.closest('input') ||
            target.closest('button') ||
            target.closest('.v-menu') ||
            target.closest('.icon-checkbox-toggle')
          ) {
            return false;
          }
          return true;
        },
      });

      this.selecto.on('select', (e) => {
        e.added.forEach((el) => {
          this.selectoToggle(el, true);
        });
        e.removed.forEach((el) => {
          this.selectoToggle(el, false);
        });
      });
    },

    selectoToggle(el, selected) {
      const triggerId = el.closest('.item-card-drag')?.id;
      if (!triggerId) return;

      const trigger = this.singleTrigger(triggerId);
      if (!trigger || trigger.selected === selected) return;

      this.updateTriggerSelection({ id: triggerId, selected });
    },

    destroySelecto() {
      if (!this.selecto) return;
      this.selecto.destroy();
      this.selecto = null;
    },
  },
  mounted() {
    this.prepareTriggers();
    this.triggerScroll();
    this.$nextTick(() => this.initSelecto());

    EventBus.$on('changeTriggersOrder', this.handleOrderChange);
  },
  beforeDestroy() {
    EventBus.$off('changeTriggersOrder', this.handleOrderChange);
    this.destroySelecto();
  },
};
</script>

<style lang="less">
.vue-portal-target {
  .format-list__item {
    background-color: #fff;

    &:hover {
      background-color: var(--v-lightGray-base);
      box-shadow: 0 1px 6px rgba(27, 192, 175, 0.5);
    }
  }
}

.campaign-triggers--block {
  padding: 16px 32px 16px 16px;
  background-color: var(--v-darkGray-base);

  .campaign-triggers__dropzone {
    min-height: 0 !important;
  }
}

.time-separator {
  height: 20px;
  position: relative;
  margin-bottom: 12px;

  &:before {
    display: block;
    content: '';
    background-color: var(--v-darkGray-base);
    height: 2px;
    width: 100%;
    position: absolute;
    left: 0;
    top: calc(50% - 1px);
  }
  span {
    position: relative;
    font-weight: 600;
    font-size: 1rem;
    background-color: var(--v-background-base);
    padding: 0 5px;
    z-index: 1;
    margin-left: 52px;
  }
}

.format-list,
.campaign-triggers {
  .sortable-ghost.content-line {
    position: relative;
    margin-top: 0 !important;

    &:before {
      content: '';
      position: absolute;
      z-index: 1;
      top: 0;
      left: 0;
      height: 100%;
      width: 100%;
      background: var(--v-darkGray-base);
    }
  }
}

.campaign-triggers {
  .sortable-ghost.content-line {
    height: 112px;
    margin-bottom: 10px;
  }
}

.campaign-triggers > .campaign-triggers__dropzone,
.campaign-triggers-list
  .field-block:only-child
  .campaign-triggers__chunk:last-child
  .campaign-triggers__dropzone {
  min-height: calc(100vh - 600px);
}

.campaign-triggers-list .field-block:not(:first-child) {
  margin-top: 24px;
}

.v-expansion-panels.triggers-panels {
  margin-top: 16px;

  .v-expansion-panel-header {
    background-color: var(--v-light-base) !important;
    box-shadow: @content-shadow;

    div.content-line {
      background-color: transparent !important;
    }
  }

  .v-expansion-panel-content__wrap {
    padding: 0 12px 12px;
    background: var(--v-darkGray-base);
  }
}

.selecto-selection {
  background: rgba(var(--v-primary-base), 0.3);
  border: 1px solid var(--v-primary-base);
}
</style>
